import React from 'react';

import type { FormField } from '../types/pdf.types';

interface TextAreaFieldOverlayProps {
  field: FormField;
  value: string;
  onChange: (value: string) => void;
  scale: number;
  isZooming?: boolean;
}

const TextAreaFieldOverlay: React.FC<TextAreaFieldOverlayProps> = ({
  field,
  value,
  onChange,
  scale,
  isZooming = false,
}) => {
  if (field.hidden) return null;

  const [x, y, width, height] = field.rect;
  const scaledX = x * scale;
  const scaledY = y * scale;
  const scaledWidth = width * scale;
  const scaledHeight = height * scale;

  const isReadOnly = field.readOnly === true;
  const isRequired = field.required === true;
  const isNoExport = field.noExport === true;
  const isRichText = field.richText === true;

  const fontSizePt = (field.fontSize || 12) * 0.75;

  let overlayBorder = '1px solid transparent';
  if (isRequired) {
    overlayBorder = '1px solid #faad14';
  } else if (isNoExport) {
    overlayBorder = '1px dashed #b37feb';
  }

  const positionedStyle = {
    left: `${scaledX}px`,
    top: `${scaledY}px`,
    width: `${scaledWidth}px`,
    height: `${scaledHeight}px`,
    opacity: isZooming ? 0 : 1,
    pointerEvents:
      isZooming || isReadOnly ? ('none' as const) : ('auto' as const),
  };

  const baseFieldStyle = {
    padding: '2px 0px',
    border: overlayBorder,
    backgroundColor: isReadOnly ? 'rgb(240, 240, 240)' : 'rgb(255, 255, 255)',
    fontFamily: field.fontFamily || 'Helvetica, Arial, sans-serif',
    fontSize: `${fontSizePt * scale}pt`,
    lineHeight: '1.4',
    textAlign: field.alignment || 'left',
    cursor: isReadOnly ? 'not-allowed' : 'text',
    overflow: field.doNotScroll ? 'hidden' : 'auto',
  };

  const titleParts = [field.name];
  if (isRequired) titleParts.push('Required');
  if (isNoExport) titleParts.push('No export');
  if (isReadOnly) titleParts.push('Read-only');
  if (isRichText) titleParts.push('Rich text not supported');
  const title = titleParts.join(' • ');

  if (isRichText) {
    return (
      <div
        className="pdf-field-overlay pdf-field-textarea-wrap"
        style={positionedStyle}
      >
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="pdf-field-textarea"
          style={baseFieldStyle}
          title={title}
          {...(field.maxLength && field.maxLength > 0
            ? { maxLength: field.maxLength }
            : {})}
          readOnly={isReadOnly}
          required={isRequired}
          aria-required={isRequired}
          spellCheck={field.doNotSpellCheck ? false : undefined}
        />
        <div className="pdf-field-rich-badge">Rich text</div>
      </div>
    );
  }

  return (
    <div
      className="pdf-field-overlay pdf-field-textarea-wrap"
      style={positionedStyle}
    >
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pdf-field-textarea"
        style={baseFieldStyle}
        title={title}
        {...(field.maxLength && field.maxLength > 0
          ? { maxLength: field.maxLength }
          : {})}
        readOnly={isReadOnly}
        required={isRequired}
        aria-required={isRequired}
        spellCheck={field.doNotSpellCheck ? false : undefined}
      />
    </div>
  );
};

const areEqual = (
  prevProps: TextAreaFieldOverlayProps,
  nextProps: TextAreaFieldOverlayProps,
) => {
  return (
    prevProps.field.id === nextProps.field.id &&
    prevProps.field.readOnly === nextProps.field.readOnly &&
    prevProps.value === nextProps.value &&
    prevProps.scale === nextProps.scale &&
    prevProps.isZooming === nextProps.isZooming
  );
};

export default React.memo(TextAreaFieldOverlay, areEqual);
